import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { Plus, Search, Filter, AlertCircle } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet } from '@/lib/api'
import { cn, formatDate, PRIORITY_COLORS, PRIORITY_LABELS, STATUS_COLORS, STATUS_LABELS, timeAgo } from '@/lib/utils'

export function WorkOrdersPage() {
  const { currentPropertyId } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')

  const status = searchParams.get('status') ?? undefined
  const priority = searchParams.get('priority') ?? undefined
  const overdueOnly = searchParams.get('overdueOnly') === 'true'
  const page = parseInt(searchParams.get('page') ?? '1', 10)

  const { data, isLoading } = useQuery({
    queryKey: ['work-orders', currentPropertyId, { status, priority, overdueOnly, search, page }],
    queryFn: () =>
      apiGet<{ data: any[]; pagination: any }>(`/properties/${currentPropertyId}/work-orders`, {
        status, priority, overdueOnly, search: search || undefined, page, limit: 25,
      }),
    enabled: !!currentPropertyId,
  })

  const STATUSES = ['NEW', 'ASSIGNED', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED', 'VERIFIED']
  const PRIORITIES = ['EMERGENCY', 'URGENT', 'HIGH', 'STANDARD', 'LOW']

  const setFilter = (key: string, value: string | null) => {
    const p = new URLSearchParams(searchParams)
    if (value) p.set(key, value)
    else p.delete(key)
    p.set('page', '1')
    setSearchParams(p)
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">Work Orders</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {data?.pagination.total ?? 0} total
          </p>
        </div>
        <Link to="/work-orders/new" className="btn-primary">
          <Plus className="w-4 h-4" />
          New Work Order
        </Link>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search work orders…"
              className="input pl-9"
            />
          </div>

          {/* Status filter */}
          <select
            value={status ?? ''}
            onChange={(e) => setFilter('status', e.target.value || null)}
            className="input w-auto"
          >
            <option value="">All Statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>

          {/* Priority filter */}
          <select
            value={priority ?? ''}
            onChange={(e) => setFilter('priority', e.target.value || null)}
            className="input w-auto"
          >
            <option value="">All Priorities</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
            ))}
          </select>

          {/* Overdue toggle */}
          <button
            onClick={() => setFilter('overdueOnly', overdueOnly ? null : 'true')}
            className={cn('btn-secondary', overdueOnly && 'border-red-400 text-red-600 bg-red-50')}
          >
            <AlertCircle className="w-4 h-4" />
            Overdue only
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : data?.data.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No work orders found</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">WO #</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Title</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Priority</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Status</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Asset / Room</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Assigned To</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">SLA</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data?.data.map((wo: any) => (
                <tr key={wo.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <Link to={`/work-orders/${wo.id}`} className="text-xs font-mono text-navy-600 hover:text-navy-800">
                      {wo.woNumber}
                    </Link>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <Link to={`/work-orders/${wo.id}`} className="text-sm font-medium text-navy-800 hover:text-navy-600 truncate block">
                      {wo.title}
                    </Link>
                    {wo.pmsGuestVipLevel && (
                      <span className="badge bg-gold-100 text-gold-700 mt-0.5">
                        VIP: {wo.pmsGuestVipLevel}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={PRIORITY_COLORS[wo.priority]}>{PRIORITY_LABELS[wo.priority]}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={STATUS_COLORS[wo.status]}>{STATUS_LABELS[wo.status]}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs text-gray-700 truncate max-w-[160px]">
                      {wo.asset?.name ?? wo.room ? `Room ${wo.room?.number}` : '—'}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {wo.assignedTo
                      ? `${wo.assignedTo.firstName} ${wo.assignedTo.lastName}`
                      : <span className="text-gray-300">Unassigned</span>}
                  </td>
                  <td className="px-4 py-3">
                    {wo.slaDeadline ? (
                      <span className={cn('text-xs font-medium',
                        new Date(wo.slaDeadline) < new Date() && !['COMPLETED','VERIFIED','CANCELLED'].includes(wo.status)
                          ? 'text-red-600' : 'text-gray-500')}>
                        {formatDate(wo.slaDeadline, 'HH:mm dd MMM')}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{timeAgo(wo.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {data && data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Showing {((page - 1) * 25) + 1}–{Math.min(page * 25, data.pagination.total)} of {data.pagination.total}
            </p>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setFilter('page', String(page - 1))}
                className="btn-secondary py-1 px-3 text-xs disabled:opacity-30"
              >
                Previous
              </button>
              <button
                disabled={page >= data.pagination.totalPages}
                onClick={() => setFilter('page', String(page + 1))}
                className="btn-secondary py-1 px-3 text-xs disabled:opacity-30"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
